/**
 * TemplateHTMLPreview
 * Renders a print template's sections as styled HTML using mock sample data.
 * Live — no save required. Mirrors the SMS template preview pattern.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';

// ─── Sample data (mirrors tokenCatalog keys) ───────────────────────────────
const MOCK = {
  'practice.name':         'Visionary Eye Care',
  'practice.address':      '123 Main Street, Suite 100',
  'practice.city':         'Toronto',
  'practice.province':     'ON',
  'practice.postalCode':   'M5V 2T6',
  'practice.phone':        '(416) 555-1234',
  'practice.email':        'info@visionaryeyecare.com',
  'practice.website':      'www.visionaryeyecare.com',
  'practice.licenseNo':    'OD-12345',
  'customer.firstName':    'Jane',
  'customer.lastName':     'Smith',
  'customer.fullName':     'Jane Smith',
  'customer.dob':          'March 12, 1985',
  'customer.phone':        '(416) 555-9876',
  'customer.email':        'jane.smith@email.com',
  'customer.address':      '456 Oak Avenue, Toronto, ON  M4B 1T3',
  'customer.healthCard':   '1234 567 890',
  'invoice.number':        'INV-2024-00123',
  'invoice.date':          'April 24, 2026',
  'invoice.dueDate':       'May 24, 2026',
  'invoice.status':        'Paid',
  'invoice.subtotal':      '$420.00',
  'invoice.tax':           '$54.60',
  'invoice.total':         '$474.60',
  'invoice.balance':       '$0.00',
  'invoice.notes':         'Thank you for your business!',
  'payment.total':         '$474.60',
  'payment.method':        'Credit Card',
  'payment.date':          'April 24, 2026',
  'rx.examDate':           'April 24, 2026',
  'rx.expiryDate':         'April 24, 2027',
  'rx.practitioner':       'Dr. Michael Chen, OD',
  'rx.sphere.od':          '-2.00',
  'rx.sphere.os':          '-1.75',
  'rx.cylinder.od':        '-0.50',
  'rx.cylinder.os':        '-0.25',
  'rx.axis.od':            '180',
  'rx.axis.os':            '175',
  'rx.add.od':             '+1.50',
  'rx.add.os':             '+1.50',
  'rx.pd':                 '62/60',
  'rx.notes':              'Wear full time.',
  'insurance.carrier':     'Green Shield Canada',
  'insurance.planId':      'GS-987654',
  'insurance.memberId':    '001',
  'insurance.policyHolder':'Robert Smith',
  'insurance.claimed':     '$350.00',
  'insurance.approved':    '$280.00',
};

function interpolate(text) {
  if (text === null || text === undefined) return '';

  let normalized = text;
  if (typeof normalized !== 'string') {
    if (typeof normalized === 'number' || typeof normalized === 'boolean') {
      normalized = String(normalized);
    } else {
      return '';
    }
  }

  return normalized.replace(/\{\{([^}]+)\}\}/g, (_, key) => MOCK[key.trim()] ?? `[${key.trim()}]`);
}

// ─── Sample rows for item/payment tables ────────────────────────────────────
const SAMPLE_ITEMS = [
  { description: 'Progressive Lenses (1.67 Index)', qty: 1, price: '$220.00', discount: '$0.00', total: '$220.00' },
  { description: 'Anti-Reflective Coating',         qty: 1, price: '$80.00',  discount: '$0.00', total: '$80.00'  },
  { description: 'Ray-Ban RB5154 — Tortoise',       qty: 1, price: '$180.00', discount: '$30.00',total: '$150.00' },
];
const SAMPLE_PAYMENTS = [
  { date: 'April 24, 2026', method: 'Visa •••• 4242', amount: '$474.60' },
];

// ─── Section renderers ───────────────────────────────────────────────────────
const css = `
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 13px; color: #222; }
  .page { background: #fff; padding: 32px 40px; min-height: 297mm; }
  h1,h2,h3 { margin: 0 0 4px 0; }
  .logo-placeholder { display:inline-block; background:#ddd; color:#888; padding:8px 16px; border-radius:4px; font-size:11px; margin-bottom:4px; }
  table { width: 100%; border-collapse: collapse; margin-top: 6px; }
  th { background: #f5f5f5; font-weight: 600; text-align: left; padding: 5px 8px; border-bottom: 2px solid #ccc; font-size:12px; }
  td { padding: 5px 8px; border-bottom: 1px solid #eee; font-size:12px; vertical-align:top; }
  td.right, th.right { text-align: right; }
  .totals-row td { font-weight: 600; border-top: 2px solid #ccc; }
  .section-title { font-size: 11px; font-weight: 700; text-transform: uppercase; color: #888; letter-spacing: .5px; margin-bottom: 4px; }
  .two-col { display: flex; gap: 32px; }
  .two-col > div { flex: 1; }
  .notes-box { background: #fafafa; border: 1px solid #e0e0e0; border-radius: 4px; padding: 10px 12px; margin-top: 4px; white-space: pre-wrap; }
  hr.divider { border: none; border-top: 1px solid #ddd; margin: 0; }
  .footer-text { font-size: 11px; color: #666; text-align: center; }
  .rx-grid { display: grid; grid-template-columns: 80px 80px 80px 80px 80px 80px; gap: 0; }
  .rx-cell { border: 1px solid #ddd; padding: 4px 6px; font-size: 11px; text-align: center; }
  .rx-header { background: #f5f5f5; font-weight: 600; }
`;

function SectionRenderer({ section, docType, cols }) {
  const { type, title, content, showLogo, height, columns } = section;

  // SectionConfig stores this as "align"; keep "alignment" fallback for older data.
  const textAlign = section.align || section.alignment || 'left';
  const activeColumns = columns || {
    description: true, qty: true, price: true, discount: true, total: true
  };

  switch (type) {
    case 'header':
      return (
        <div style={{ textAlign }}>
          {showLogo && <div className="logo-placeholder">Practice Logo</div>}
          {content
            ? <div dangerouslySetInnerHTML={{ __html: interpolate(content).replace(/\n/g, '<br/>') }} />
            : <>
                <div style={{ fontWeight: 700, fontSize: 18 }}>{MOCK['practice.name']}</div>
                <div>{MOCK['practice.address']}</div>
                <div>{MOCK['practice.city']}, {MOCK['practice.province']} &nbsp; {MOCK['practice.postalCode']}</div>
                <div>{MOCK['practice.phone']} &nbsp;|&nbsp; {MOCK['practice.email']}</div>
              </>
          }
          {title && <div className="section-title" style={{ marginTop: 6 }}>{title}</div>}
        </div>
      );

    case 'customer':
      return (
        <div className="two-col" style={{ textAlign }}>
          <div>
            {title && <div className="section-title">{title || 'Patient'}</div>}
            <div><strong>{MOCK['customer.fullName']}</strong></div>
            <div>DOB: {MOCK['customer.dob']}</div>
            <div>{MOCK['customer.phone']}</div>
            <div>{MOCK['customer.email']}</div>
          </div>
          <div style={{ textAlign }}>
            <div><strong>{docType === 'rx' ? 'Prescription Date:' : 'Invoice #:'}</strong></div>
            <div>{docType === 'rx' ? MOCK['rx.examDate'] : MOCK['invoice.number']}</div>
            <div><strong>Date:</strong></div>
            <div>{MOCK['invoice.date']}</div>
          </div>
        </div>
      );

    case 'items':
      return (
        <>
          {title && <div className="section-title">{title || 'Items'}</div>}
          <table>
            <thead>
              <tr>
                {activeColumns.description !== false && <th>Description</th>}
                {activeColumns.qty        !== false && <th className="right">Qty</th>}
                {activeColumns.price      !== false && <th className="right">Price</th>}
                {activeColumns.discount   !== false && <th className="right">Discount</th>}
                {activeColumns.total      !== false && <th className="right">Total</th>}
              </tr>
            </thead>
            <tbody>
              {SAMPLE_ITEMS.map((row, i) => (
                <tr key={i}>
                  {activeColumns.description !== false && <td>{row.description}</td>}
                  {activeColumns.qty         !== false && <td className="right">{row.qty}</td>}
                  {activeColumns.price       !== false && <td className="right">{row.price}</td>}
                  {activeColumns.discount    !== false && <td className="right">{row.discount}</td>}
                  {activeColumns.total       !== false && <td className="right">{row.total}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </>
      );

    case 'totals':
      {
      const tableAlignStyle =
        textAlign === 'center'
          ? { width: '50%', marginLeft: 'auto', marginRight: 'auto' }
          : textAlign === 'right'
            ? { width: '50%', marginLeft: 'auto' }
            : { width: '50%', marginRight: 'auto' };

      return (
        <>
          {title && <div className="section-title" style={{ textAlign }}>{title || 'Summary'}</div>}
          <table style={tableAlignStyle}>
            <tbody>
              <tr><td>Subtotal</td><td className="right">{MOCK['invoice.subtotal']}</td></tr>
              <tr><td>HST (13%)</td><td className="right">{MOCK['invoice.tax']}</td></tr>
              <tr className="totals-row"><td>Total</td><td className="right">{MOCK['invoice.total']}</td></tr>
              <tr><td>Balance Due</td><td className="right">{MOCK['invoice.balance']}</td></tr>
            </tbody>
          </table>
        </>
      );
      }

    case 'payments':
      return (
        <>
          {title && <div className="section-title">{title || 'Payments'}</div>}
          <table>
            <thead><tr><th>Date</th><th>Method</th><th className="right">Amount</th></tr></thead>
            <tbody>
              {SAMPLE_PAYMENTS.map((p, i) => (
                <tr key={i}>
                  <td>{p.date}</td>
                  <td>{p.method}</td>
                  <td className="right">{p.amount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      );

    case 'prescription': {
      const fields = [
        { label: 'Sphere', od: MOCK['rx.sphere.od'],   os: MOCK['rx.sphere.os'] },
        { label: 'Cylinder',od: MOCK['rx.cylinder.od'], os: MOCK['rx.cylinder.os'] },
        { label: 'Axis',    od: MOCK['rx.axis.od'],     os: MOCK['rx.axis.os'] },
        { label: 'Add',     od: MOCK['rx.add.od'],      os: MOCK['rx.add.os'] },
      ];
      return (
        <>
          {title && <div className="section-title">{title || 'Prescription'}</div>}
          <table>
            <thead>
              <tr><th></th><th>Sphere</th><th>Cylinder</th><th>Axis</th><th>Add</th></tr>
            </thead>
            <tbody>
              <tr><td><strong>OD (Right)</strong></td>{fields.map(f=><td key={f.label} className="right">{f.od}</td>)}</tr>
              <tr><td><strong>OS (Left)</strong></td>{fields.map(f=><td key={f.label} className="right">{f.os}</td>)}</tr>
            </tbody>
          </table>
          <div style={{ marginTop: 6, fontSize: 12 }}>PD: {MOCK['rx.pd']} &nbsp;|&nbsp; Notes: {MOCK['rx.notes']}</div>
          <div style={{ marginTop: 4, fontSize: 12 }}>Practitioner: {MOCK['rx.practitioner']} &nbsp;&nbsp; Expires: {MOCK['rx.expiryDate']}</div>
        </>
      );
    }

    case 'insurance':
      return (
        <>
          {title && <div className="section-title">{title || 'Insurance'}</div>}
          <div className="two-col">
            <div>
              <div>Carrier: <strong>{MOCK['insurance.carrier']}</strong></div>
              <div>Plan ID: {MOCK['insurance.planId']}</div>
              <div>Member ID: {MOCK['insurance.memberId']}</div>
            </div>
            <div>
              <div>Policy Holder: {MOCK['insurance.policyHolder']}</div>
              <div>Claimed: {MOCK['insurance.claimed']}</div>
              <div>Approved: {MOCK['insurance.approved']}</div>
            </div>
          </div>
        </>
      );

    case 'notes':
      return (
        <>
          {title && <div className="section-title">{title || 'Notes'}</div>}
          <div className="notes-box">
            {content ? interpolate(content) : MOCK['invoice.notes']}
          </div>
        </>
      );

    case 'footer':
      return (
        <div className="footer-text" style={{ textAlign }}>
          {content
            ? <span dangerouslySetInnerHTML={{ __html: interpolate(content).replace(/\n/g, '<br/>') }} />
            : `Thank you for choosing ${MOCK['practice.name']}`
          }
        </div>
      );

    case 'divider':
      return <hr className="divider" />;

    case 'spacer':
      return <div style={{ height: height || 24 }} />;

    default:
      return (
        <div style={{ padding: '6px 0', color: '#999', fontSize: 12 }}>
          [{type}] {content ? interpolate(content) : ''}
        </div>
      );
  }
}

export default function TemplateHTMLPreview({ sections = [], documentType = 'invoice', pageSettings = {} }) {
  return (
    <Box
      sx={{
        background: '#e8e8e8',
        p: 3,
        minHeight: '100%',
        display: 'flex',
        justifyContent: 'center',
        overflowY: 'auto',
      }}
    >
      {/* A4-ish paper */}
      <Box
        sx={{
          background: '#fff',
          width: 595,            // A4 at 72dpi pt width
          minHeight: 842,
          boxShadow: '0 2px 12px rgba(0,0,0,.18)',
          p: '32px 40px',
          fontFamily: 'Arial, Helvetica, sans-serif',
          fontSize: 13,
          color: '#222',
          position: 'relative',
        }}
      >
        {/* Inject base CSS once */}
        <style>{css}</style>

        {sections.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8, color: 'text.disabled' }}>
            <Typography variant="body2">Add sections from the palette to see a preview.</Typography>
          </Box>
        ) : (
          sections.map((section, i) => (
            <Box key={i} sx={{ mb: '12px' }}>
              <SectionRenderer section={section} docType={documentType} />
            </Box>
          ))
        )}

        <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #eee', fontSize: 11, color: '#aaa', textAlign: 'center' }}>
          Preview uses sample data — actual output will use real record values.
        </Box>
      </Box>
    </Box>
  );
}
